import { ChangeEvent, useState, useCallback } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import debounce from "lodash/debounce";

import { useRouter } from "next/router";

import {
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  InputRightElement,
  InputGroup,
  CheckIcon,
  Spinner,
  Input,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";

import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import { setError } from "@/redux/features/questions/slice";

import { verifyUserAvailability } from "@/models";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import useEmbeds from "@/hooks/useEmbeds";
import usePinned from "@/hooks/usePinned";
import type { SurveyParams } from "@/types";
import useUser from "@/hooks/useUser";
import { stripDangerousHtml } from "@/lib";

interface AccountEmailQuestionProps {
  question: Partial<Question>;
  isAsterixRequired?: boolean;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  schema = schema.email();
  try {
    await schema.max(question.maxLength || 255).validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const AccountEmailQuestion = ({
  question,
  isAsterixRequired = true
}: AccountEmailQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [isValidate, setisValidate] = useState(false);
  const [isEmailAvailable, setEmailAvailability] = useState(false);
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { signupFormResponses } = useAppSelector(
    (state) => state.signupFormResponses
  );
  const { toolType } = useAppSelector((state) => state?.user?.pageData);
  const { isRegisteredUserLoggedIn } = useUser();
  const { isPinned } = usePinned();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();
  const { query, asPath } = useRouter();
  const { projectId, surveyId } = query as Partial<SurveyParams>;

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setisValidate(true);

    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );

    if (e.target.value === signupFormResponses[question.id]?.value) {
      return setisValidate(false);
    }

    handleEmailVerification(e.target.value);
  };

  const validateEmail = async (value: string) => {
    const error = await validate(value, question);

    if (error?.length) {
      dispatch(
        setError({
          questionId: question.id,
          error
        })
      );
    } else {
      const response = await verifyUserAvailability(token, baseUrl, {
        email: value
      });

      if (response.emailAvailable) {
        setEmailAvailability(true);
      } else {
        dispatch(
          setError({
            questionId: question.id,
            error: "emailInUse"
          })
        );
      }
    }
    setisValidate(false);
  };

  const handleEmailVerification = useCallback(
    debounce(validateEmail, 1000),
    []
  );

  const getRoute = () => {
    if (isPinned && toolType === "SurveyTool") {
      return `/login?next=${encodeURIComponent(`/${projectId}?tool=survey_tool&tool_id=${surveyId}#tool_tab`)}`;
    }
    return isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? `/login`
      : `/login?next=${encodeURIComponent(asPath)}`;
  };

  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required && isAsterixRequired}
      className={error.length > 0 ? "ehqQuestionError" : null}
      isDisabled={status === "saving"}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <InputGroup>
        <Input
          type="email"
          value={answer}
          onChange={handleChange}
          data-testid="accountEmailQuestion-input"
          placeholder={question?.placeholder}
          maxLength={question.maxLength}
          size={{ base: "lg", lg: "md" }}
          name="accountEmailQuestion-input"
          autoComplete="off"
        />
        {answer && (
          <InputRightElement height="100%">
            {isValidate && <Spinner size="sm" />}
            {isEmailAvailable && !error.length && !isValidate && (
              <CheckIcon color="green.500" />
            )}
          </InputRightElement>
        )}
      </InputGroup>
      {error.length > 0 && (
        <FormErrorMessage data-testid="accountEmailQuestion-errorMessage">
          <FormErrorIcon />
          {error === "emailInUse" && !isRegisteredUserLoggedIn ? (
            /* 
            TODO: Replace this Box component with a new simple HTMLWrapper component.
            The current HTMLWrapper component should be renamed to 'HTMLWrapperForFroala' 
            (or a similar, more descriptive name) to reflect its specific usage.
            */
            <Box
              dangerouslySetInnerHTML={{
                __html: stripDangerousHtml(
                  translate("errors.emailInUsePleaseLogin", {
                    loginRoute: getRoute(),
                    target: getRouteTarget()
                  })
                )
              }}
            />
          ) : (
            translate(`errors.${error}`)
          )}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default AccountEmailQuestion;
